import { Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSendAppointmentCheckInEmail } from '../../../API/GoMoto';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization, useLocale } from '../../../context/Localization/localization';
import { useSetHeaderProps, useSetKioskBannerVisibility } from '../../../context/Page/page';
import { GMCapitalize } from '../../../controls/Case';
import { CTAButton } from '../../../controls/ButtonBase';
import { Heading } from '../../../controls/Heading/Heading';
import { IconButton } from '../../../controls/IconButton/IconButton';
import { Spinner } from '../../../controls/Spinner/Spinner';
import css from './EmailSearch.module.scss';
import { nameof } from '../../../helpers/nameof';
import { isNumeric } from '../../../helpers/Number';
import { GMFormPhoneOrEmailField } from '../../../controls/TextField';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { GMForm, GMFormWrapper } from '../../../controls/Form';
import { EmailSearchProps, HeaderType } from './EmailSearch.types';
import { maskValue } from '../../../helpers/maskValue';

/**
 * Searches for an appointment by email. Used for the GoMoto 365 outdoor kiosk
 */
export const EmailSearch: React.FC<EmailSearchProps> = React.memo((props) => {
    const Localization = useLocalization();
    const locale = useLocale();
    const {
        key: kioskKey,
        dealer: { key: dealerKey },
    } = useKioskState();
    const navigate = useNavigate();
    const [emailSent, setEmailSent] = useState<boolean>(false);
    const [email, setEmail] = useState<string | undefined>('');
    const [error, setError] = useState<boolean>(false);
    const [headerType, setHeaderType] = useState<HeaderType | undefined>(undefined);
    const send = useSendAppointmentCheckInEmail();
    const showBanner = useSetKioskBannerVisibility();
    const setHeader = useSetHeaderProps();

    const initialValues = {
        phoneNumberOrEmail: '',
        kioskKey,
        dealerKey,
    };

    const onAlternateSearch = (email: string) => {
        showBanner(true);
        setHeader(undefined);
        props.onAlternateSearch && props.onAlternateSearch(email);
    };

    useEffect(() => {
        const currentHeader: string | undefined = headerType === 'CHECK_EMAIL' ?
            Localization.CheckEmail : headerType === 'RECORD_NOT_FOUND' ?
                Localization.RecordNotFound : undefined;
        if (currentHeader) {
            setHeader({
                title: currentHeader,
                rightIcons: [
                    <IconButton
                        key={'em-sr-close'}
                        sizedIcon={
                            <GMSizedIcon
                                iconType={'Close'}
                                iconSize={'medium'}
                                ariaLabel={Localization.CloseIcon}
                                title={Localization.ClickToCloseWindow}
                            />
                        }
                        onClick={() => navigate(-1)}
                        ariaLabel={Localization.ClickToCloseWindow}
                        id={Localization.ClickToCloseWindow}
                    />,
                ],
            });
        }
        showBanner(false);

        return () => {
            setHeader(undefined);
            showBanner(true);
        };

    }, [setHeader, showBanner, navigate, Localization, headerType, locale]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async ({ ...filter }) => {
                await send({
                    ...filter,
                    email: filter.phoneNumberOrEmail.trim(),
                })
                    .then(
                        (value) => {
                            setHeaderType('CHECK_EMAIL');
                            setEmailSent(true);
                            setEmail(value);
                        },
                        () => {
                            setHeaderType('RECORD_NOT_FOUND');
                            setError(true);
                        },
                    );
            }}
            validationSchema={Yup.object()
                .shape({
                    [nameof<typeof initialValues>('phoneNumberOrEmail')]: Yup.lazy((val) => {
                        return (!(/[^0-9]/).test(val as string)
                            ? Yup.string()
                                .trim()
                                .phone(Localization.EmailOrPhoneInvalid)
                                .nullable()
                            : Yup.string()
                                .trim()
                                .email(Localization.EmailOrPhoneInvalid)
                                .nullable()
                        );
                    }),
                })}
        >
            {({ values, isSubmitting, setValues }) => (
                <GMForm
                    backButton={
                        emailSent || error ? (
                            <CTAButton
                                type={'button'}
                                styleType={'secondary'}
                                kioskOptions={{
                                    inlineOptions: {
                                        sizedIcon: (
                                            <GMSizedIcon
                                                iconSize={'custom'}
                                                iconType={'Arrow'}
                                                ariaLabel={Localization.ProceedArrow}
                                            />
                                        ),
                                    },
                                }}
                                title={Localization.ClickToReturnToLookup}
                                label={Localization.AlternateSearch}
                                onClick={() => onAlternateSearch(values.phoneNumberOrEmail)}
                                id={Localization.ClickToReturnToLookup}
                                ariaLabel={Localization.ClickToReturnToLookup}
                            />
                        ) : undefined
                    }
                    submitButton={
                        !emailSent ? (
                            <CTAButton
                                type={'submit'}
                                styleType={'primary'}
                                kioskOptions={{
                                    inlineOptions: {
                                        sizedIcon: (
                                            <GMSizedIcon
                                                iconSize={'custom'}
                                                iconType={'Arrow'}
                                                ariaLabel={Localization.ProceedArrow}
                                            />
                                        ),
                                    },
                                }}
                                title={Localization.ClickToSendEmailReceipt}
                                label={Localization.SendEmail}
                                id={Localization.ClickToSendEmailReceipt}
                                ariaLabel={Localization.ClickToSendEmailReceipt}
                            />
                        ) : undefined
                    }
                >
                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                    <>
                        {error ? (
                            <>
                                <h2>
                                    <GMCapitalize>{Localization.AppointmentNotFound}</GMCapitalize>
                                </h2>
                                <p>
                                    {
                                        Localization.formatString(
                                            Localization.AppointmentNotFoundFromEmail,
                                            !values.phoneNumberOrEmail || isNumeric(values.phoneNumberOrEmail) ? maskValue(email) : <a href={'mailto:' + email}>{email}</a>,
                                        )
                                    }
                                </p>
                                <Heading showMustache />
                            </>
                        ) : emailSent ? (
                            <>
                                <h2>{Localization.EmailSent}</h2>
                                <p>
                                    {Localization.formatString(
                                        Localization.CheckInEmailSent,
                                        !values.phoneNumberOrEmail || isNumeric(values.phoneNumberOrEmail) ? maskValue(email) : <a href={'mailto:' + email}>{email}</a>,
                                        <b>{Localization.CheckInNow}</b>,
                                    )}
                                </p>
                                <p>{Localization.EmailTrouble}</p>
                            </>
                        ) : (
                            <Heading
                                primaryHeading={Localization.Excellent}
                                secondaryHeading={Localization.PleaseEnterYourEmail}
                                roundedIcon={
                                    <GMRoundedIcon
                                        roundedBackgroundSize={'large'}
                                        themeType={'secondary'}
                                    >
                                        <GMSizedIcon iconType={'Appointment'} iconSize={'extraLarge'} />
                                    </GMRoundedIcon>
                                }
                                showMustache
                            />
                        )}
                        {isSubmitting && <Spinner />}

                        {!emailSent && (
                            <div className={css.emailField}>
                                <GMFormPhoneOrEmailField
                                    name={nameof<typeof values>('phoneNumberOrEmail')}
                                    id={Localization.PhoneNumberOrEmail}
                                    title={Localization.PhoneNumberOrEmail}
                                    label={Localization.PhoneNumberOrEmail}
                                    ariaLabel={Localization.PhoneNumberOrEmail}
                                    autoFocus
                                    required
                                />
                            </div>
                        )}
                    </>
                </GMForm>
            )}
        </Formik>
    );
});
